import React from "react";
import { FiEdit2 } from "react-icons/fi";
import CallCenterModal from "./listing/CallCenterModal";
import StatusBadge from "./listing/StatusBadge";
import {
  FILTER_LABEL_CLASS,
  LISTING_PRIMARY_BUTTON_CLASS,
} from "./listing/listingStyles";
import {
  formatAccountPlanAssignmentMode,
  formatAccountPlanFrequency,
  formatAccountPlanPriority,
  formatAccountPlanReminder,
  formatAccountPlanScheduleSummary,
} from "../../utils/accountPlanLabels";

const DetailField = ({ label, value, className = "" }) => (
  <div className={className}>
    <div className={FILTER_LABEL_CLASS}>{label}</div>
    <div className="font-medium text-slate-800">{value}</div>
  </div>
);

const SectionTitle = ({ children }) => (
  <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400">{children}</h3>
);

const AccountPlanTypeDetailDrawer = ({
  open,
  planType,
  loading,
  metadata,
  canUpdate,
  onClose,
  onEdit,
  t,
}) => {
  const activeCount = Number(planType?.activeAccountPlanCount || 0);
  const totalCount = Number(planType?.totalAccountPlanCount || 0);
  const usagePercent = totalCount > 0 ? Math.round((activeCount / totalCount) * 100) : 0;

  const formatTimestamp = (value) => {
    if (!value) return t("ccEmDash");
    try {
      return new Date(value).toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return String(value);
    }
  };

  return (
    <CallCenterModal
      open={open}
      onClose={onClose}
      title={loading ? t("ccLoading") : planType?.name || t("ccEmDash")}
      subtitle={t("ccAccountPlanTypeDetailSubtitle")}
      maxWidth="2xl"
      maxHeight="90vh"
      zIndexClass="z-[190]"
      closeLabel={t("ccClose")}
      contentVariant="none"
      bodyClassName="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6"
      headerBelow={
        planType ? (
          <StatusBadge
            status={planType.active ? "ACTIVE" : "CANCELLED"}
            label={planType.active ? t("ccActive") : t("ccInactive")}
          />
        ) : null
      }
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="h-10 rounded-xl border border-slate-200 px-4 text-sm font-semibold text-slate-600 hover:bg-slate-50"
          >
            {t("ccClose")}
          </button>
          {canUpdate && planType ? (
            <button type="button" onClick={() => onEdit(planType)} className={LISTING_PRIMARY_BUTTON_CLASS}>
              <FiEdit2 className="h-4 w-4" />
              {t("ccEdit")}
            </button>
          ) : null}
        </>
      }
    >
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((item) => (
            <div key={item} className="h-16 animate-pulse rounded-xl bg-slate-100" />
          ))}
        </div>
      ) : planType ? (
        <div className="space-y-6">
          {planType.description ? (
            <p className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm">
              {planType.description}
            </p>
          ) : null}

          <div className="rounded-xl border border-[#2B7FFF]/20 bg-[#2B7FFF]/5 px-4 py-3">
            <p className="text-sm font-semibold text-[#1d4ed8]">
              {formatAccountPlanScheduleSummary(planType, metadata, t)}
            </p>
          </div>

          {totalCount > 0 ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              {t("ccAccountPlanTypeInUseWarning").replace("{count}", String(totalCount))}
            </div>
          ) : null}

          <section className="space-y-3">
            <SectionTitle>{t("ccAccountPlanTypeDetailScheduleSection")}</SectionTitle>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <DetailField
                label={t("ccAccountPlanFrequency")}
                value={formatAccountPlanFrequency(planType.frequency, metadata, t)}
              />
              <DetailField
                label={t("ccAccountPlanInstallmentCount")}
                value={planType.installmentCount}
              />
              <DetailField
                label={t("ccAccountPlanReminderOffset")}
                value={formatAccountPlanReminder(planType.reminderDaysBefore, metadata)}
              />
              <DetailField
                label={t("ccAccountPlanAssignmentMode")}
                value={formatAccountPlanAssignmentMode(planType.assignmentMode, metadata, t)}
              />
              <DetailField
                label={t("ccAccountPlanTypeDefaultAgent")}
                value={planType.defaultAssignedToName || t("ccAccountPlanAutoAssign")}
                className="sm:col-span-2"
              />
            </div>
          </section>

          <section className="space-y-3">
            <SectionTitle>{t("ccAccountPlanTypeDetailTicketSection")}</SectionTitle>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <DetailField
                label={t("ccAccountPlanTicketPriority")}
                value={formatAccountPlanPriority(planType.ticketPriority, metadata)}
              />
              {planType.remarksTemplate ? (
                <div className="sm:col-span-2">
                  <div className={FILTER_LABEL_CLASS}>{t("ccAccountPlanRemarksTemplate")}</div>
                  <p className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm">
                    {planType.remarksTemplate}
                  </p>
                </div>
              ) : (
                <DetailField label={t("ccAccountPlanRemarksTemplate")} value={t("ccEmDash")} />
              )}
            </div>
          </section>

          <section className="space-y-3">
            <SectionTitle>{t("ccAccountPlanTypeDetailUsageSection")}</SectionTitle>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="mb-2 flex items-center justify-between text-sm">
                <span className="font-medium text-slate-800">
                  {activeCount} / {totalCount} {t("ccAccountPlanTypeUsageHint")}
                </span>
                {totalCount > 0 ? (
                  <span className="text-slate-500">
                    {usagePercent}% {t("ccActive").toLowerCase()}
                  </span>
                ) : null}
              </div>
              {totalCount > 0 ? (
                <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-full rounded-full bg-[#2B7FFF] transition-all"
                    style={{ width: `${usagePercent}%` }}
                  />
                </div>
              ) : (
                <p className="text-sm text-slate-500">{t("ccAccountPlanTypeUsageEmpty")}</p>
              )}
            </div>
            <DetailField label={t("ccUpdatedAt")} value={formatTimestamp(planType.updatedAt)} />
          </section>
        </div>
      ) : (
        <p className="text-sm text-slate-500">{t("ccAccountPlanTypeLoadFailed")}</p>
      )}
    </CallCenterModal>
  );
};

export default AccountPlanTypeDetailDrawer;
